import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save } from "lucide-react";
import { MENU_TIER_LOCK_REGISTRY, MENU_TIER_LOCK_TIERS, type MenuTierLockTier } from "@shared/schema";

interface LockRow {
  id: number;
  itemKey: string;
  allowedTiers: string[];
}

interface LocksResponse {
  registry: typeof MENU_TIER_LOCK_REGISTRY;
  tiers: readonly MenuTierLockTier[];
  locks: LockRow[];
}

const ALL_TIERS_SET = new Set<string>(MENU_TIER_LOCK_TIERS);

function tierHeaderLabel(tier: string): string {
  if (tier === "PMPro") return "PM Pro";
  return tier;
}

export function MenuTierLocksPanel() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<LocksResponse>({
    queryKey: ["/api/admin/menu-tier-locks"],
  });

  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data) return;
    const next: Record<string, Set<string>> = {};
    for (const group of data.registry) {
      for (const item of group.items) {
        const row = data.locks.find((l) => l.itemKey === item.key);
        next[item.key] = row ? new Set(row.allowedTiers) : new Set(ALL_TIERS_SET);
      }
    }
    setDraft(next);
    setDirty(new Set());
  }, [data]);

  const upsertMutation = useMutation({
    mutationFn: async (payload: { itemKey: string; allowedTiers: string[] }) => {
      await apiRequest("PUT", "/api/admin/menu-tier-locks", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/menu-tier-locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/menu-tier-locks/denied"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast({ title: "Save failed", description: msg, variant: "destructive" });
    },
  });

  function toggle(itemKey: string, tier: string) {
    setDraft((prev) => {
      const current = new Set(prev[itemKey] ?? ALL_TIERS_SET);
      if (current.has(tier)) current.delete(tier);
      else current.add(tier);
      return { ...prev, [itemKey]: current };
    });
    setDirty((prev) => new Set(prev).add(itemKey));
  }

  async function saveItem(itemKey: string) {
    const allowed = Array.from(draft[itemKey] ?? ALL_TIERS_SET);
    await upsertMutation.mutateAsync({ itemKey, allowedTiers: allowed });
    setDirty((prev) => {
      const n = new Set(prev);
      n.delete(itemKey);
      return n;
    });
    toast({ title: "Saved", description: itemKey });
  }

  async function saveAll() {
    const keys = Array.from(dirty);
    for (const key of keys) {
      const allowed = Array.from(draft[key] ?? ALL_TIERS_SET);
      // eslint-disable-next-line no-await-in-loop
      await upsertMutation.mutateAsync({ itemKey: key, allowedTiers: allowed });
    }
    setDirty(new Set());
    toast({ title: "Saved", description: `${keys.length} item(s) updated` });
  }

  if (isLoading || !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading menu tier locks…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-page-title">
            Menu Tier Locks
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Check the tiers that should see each menu / submenu item. Unchecked tiers will have the item hidden from their sidebar. Items not configured here are visible to everyone by default. Founder tiers inherit Professional's settings.
          </p>
        </div>
        <Button
          onClick={saveAll}
          disabled={dirty.size === 0 || upsertMutation.isPending}
          data-testid="button-save-all"
          data-tour="admin-menu-tier-locks-save-all"
        >
          <Save className="h-4 w-4 mr-2" />
          Save {dirty.size > 0 ? `(${dirty.size})` : "All"}
        </Button>
      </div>

      {data.registry.map((group, groupIdx) => (
        <Card
          key={group.groupKey}
          data-testid={`card-group-${group.groupKey}`}
          data-tour={groupIdx === 0 ? "admin-menu-tier-locks-table" : undefined}
        >
          <CardHeader>
            <CardTitle className="text-base">{group.groupLabel}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 w-1/3">Item</th>
                  {MENU_TIER_LOCK_TIERS.map((tier) => (
                    <th key={tier} className="text-center px-3 py-2 font-medium text-gray-600">
                      {tierHeaderLabel(tier)}
                    </th>
                  ))}
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => {
                  const allowed = draft[item.key] ?? ALL_TIERS_SET;
                  const isDirty = dirty.has(item.key);
                  return (
                    <tr key={item.key} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-4 py-2 text-gray-800" data-testid={`text-item-${item.key}`}>
                        {item.label}
                        <span className="ml-2 text-xs text-gray-400 font-mono">{item.key}</span>
                      </td>
                      {MENU_TIER_LOCK_TIERS.map((tier) => (
                        <td key={tier} className="text-center px-3 py-2">
                          <Checkbox
                            checked={allowed.has(tier)}
                            onCheckedChange={() => toggle(item.key, tier)}
                            data-testid={`checkbox-${item.key}-${tier.toLowerCase()}`}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">
                        {isDirty && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => saveItem(item.key)}
                            disabled={upsertMutation.isPending}
                            data-testid={`button-save-${item.key}`}
                          >
                            Save
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
